import {
  type RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete_Args,
  type RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete_Options,
  useRentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type { Client } from '@zg-rentals/http-client';
import type { ApiResponseErrorDto } from '@zg-rentals/ts-rental-manager-api-client';

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

const assertMultiplexAmenityDeleteMutationArgs = (
  args: RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete_Args,
): void => {
  const { postData } = args;

  if (typeof postData !== 'object' || postData === null) {
    throw new TypeError(
      'useMultiplexAmenityDelete: `postData` is required (UserPropertyMultiplexAmenityDeleteV2RequestDto).',
    );
  }

  const sessionId = postData.multiplexBuildingSessionId;
  const amenityId = postData.amenityId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexAmenityDelete: `postData.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }

  if (typeof amenityId !== 'string' || amenityId.trim() === '') {
    throw new TypeError(
      'useMultiplexAmenityDelete: `postData.amenityId` is required on every call (non-empty string).',
    );
  }
};

export type UseMultiplexAmenityDeleteOptions = Omit<
  RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexAmenityDeleteMutation = ReturnType<
  typeof useRentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete
>;

export type UseMultiplexAmenityDeleteResult = Omit<
  BaseMultiplexAmenityDeleteMutation,
  'mutate' | 'mutateAsync' | 'error'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete_Args,
    options?: Parameters<BaseMultiplexAmenityDeleteMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexAmenityDeleteMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete_Args,
    options?: Parameters<BaseMultiplexAmenityDeleteMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexAmenityDeleteMutation['mutateAsync']>;
  errors: ApiResponseErrorDto<unknown> | undefined;
};

/**
 * Multiplex additional-amenity delete: wraps POST `/api/web/v2/users/properties/multiplex/amenities/delete`.
 */
const useMultiplexAmenityDelete = (
  mutationOptions: UseMultiplexAmenityDeleteOptions = {},
): UseMultiplexAmenityDeleteResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'useRentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_delete',
        details: 'useMultiplexAmenityDelete',
        ...metaOverrides?.context,
      },
    },
  });

  const { error, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexAmenityDeleteMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexAmenityDeleteMutationArgs(args);
      return mutateAsync(args, options);
    },
    errors: toApiResponseErrorDto(error),
  };
};

export default useMultiplexAmenityDelete;
